/**
 * TransferDetails Component
 *
 * Displays transfer transaction details showing the flow from source to destination account.
 * Uses arrow visualization to show the transfer direction with account type icons.
 *
 * @see types/accounting.ts for Transaction and Entry types
 */

import { ArrowRight, Landmark, CreditCard } from 'lucide-react';
import clsx from 'clsx';
import type { Transaction } from '@/types/accounting';

export interface TransferDetailsProps {
  /** Transaction to display transfer details for */
  transaction: Transaction;
  /** Optional compact mode for inline display */
  compact?: boolean;
}

/**
 * Get the appropriate account icon based on account type
 * Landmark icon for asset accounts (banks)
 * CreditCard icon for liability accounts (credit cards)
 */
function getAccountIcon(accountType: string | undefined) {
  if (accountType === 'liability') {
    return CreditCard;
  }
  return Landmark;
}

/**
 * TransferDetails renders a visual flow showing source and destination accounts.
 *
 * Features:
 * - Displays source account (credit entry) -> destination account (debit entry)
 * - Shows account type icons (bank for asset, credit card for liability)
 * - Arrow visualization for transfer flow
 * - Compact mode for inline display
 * - Handles missing entries gracefully
 *
 * The component identifies transfer direction by entry types:
 * - Credit entry: money leaving (source account)
 * - Debit entry: money entering (destination account)
 *
 * @example
 * ```tsx
 * <TransferDetails transaction={transferTransaction} />
 * // Renders: ING Bank -> Credit Card
 * ```
 *
 * @example
 * ```tsx
 * <TransferDetails transaction={transferTransaction} compact />
 * // Renders compact inline version
 * ```
 */
export function TransferDetails({
  transaction,
  compact = false,
}: TransferDetailsProps) {
  // Find credit and debit entries
  const creditEntry = transaction.entries.find((e) => e.entryType === 'credit');
  const debitEntry = transaction.entries.find((e) => e.entryType === 'debit');

  // Return null if we don't have both entries
  if (!creditEntry || !debitEntry) {
    return null;
  }

  // Get account types from the transaction's bankAccount
  // For transfers, we need to determine which account is which type
  // The bankAccount in transaction is typically the source account
  const sourceAccountType = transaction.bankAccount?.accountType;

  // Find the destination account type by looking up the debit entry's account
  // We'll use a simple heuristic: if the account name contains "Credit Card", it's likely a liability
  const destinationAccountType = debitEntry.accountName?.toLowerCase().includes('credit card')
    ? 'liability'
    : 'asset';

  const SourceIcon = getAccountIcon(sourceAccountType);
  const DestinationIcon = getAccountIcon(destinationAccountType);

  if (compact) {
    return (
      <div
        className="inline-flex items-center gap-2 text-sm text-gray-600"
        data-testid="transfer-details"
      >
        <div className="inline-flex items-center gap-1.5">
          <SourceIcon className="w-3.5 h-3.5 text-gray-500" aria-hidden="true" />
          <span>{creditEntry.accountName}</span>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
        <div className="inline-flex items-center gap-1.5">
          <DestinationIcon className="w-3.5 h-3.5 text-gray-500" aria-hidden="true" />
          <span>{debitEntry.accountName}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg"
      data-testid="transfer-details"
    >
      {/* Source Account */}
      <div className="flex items-center gap-2 flex-1">
        <div
          className={clsx(
            'w-8 h-8 rounded-full flex items-center justify-center',
            'bg-purple-100 text-purple-600'
          )}
        >
          <SourceIcon className="w-4 h-4" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {creditEntry.accountName}
          </p>
          <p className="text-xs text-gray-500">Source</p>
        </div>
      </div>

      {/* Arrow */}
      <ArrowRight
        className="w-5 h-5 text-purple-600 flex-shrink-0"
        aria-hidden="true"
      />

      {/* Destination Account */}
      <div className="flex items-center gap-2 flex-1">
        <div
          className={clsx(
            'w-8 h-8 rounded-full flex items-center justify-center',
            'bg-purple-100 text-purple-600'
          )}
        >
          <DestinationIcon className="w-4 h-4" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {debitEntry.accountName}
          </p>
          <p className="text-xs text-gray-500">Destination</p>
        </div>
      </div>
    </div>
  );
}

export default TransferDetails;
